import { useState } from 'react'
import { BookOpen, Pencil, Plus, ThumbsDown, ThumbsUp } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useHelpdeskMutations, useKbArticles, useTicketCategories } from '../api'
import { KB_STATUS_LABELS } from '../types'
import type { KbStatus } from '../types'

/** Answers written down once, so the same question stops arriving as a ticket. */
export function KnowledgeBasePage() {
  useBreadcrumb()
  const [search, setSearch] = useState('')
  const articles = useKbArticles({ q: search })
  const categories = useTicketCategories()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const {
    saveArticle, publishArticle, rateArticle, archiveArticle,
  } = useHelpdeskMutations()

  const [draft, setDraft] = useState<{
    id?: string
    slug: string; title: string; summary: string; body: string; categoryId: string
  } | null>(null)
  const [archiving, setArchiving] = useState<{ id: string; title: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Knowledge base"
        subtitle="What the answer was last time, so the next person does not have to ask."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ slug: '', title: '', summary: '', body: '', categoryId: '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New article
          </Button>
        }
      />

      <div className="max-w-md">
        <Label htmlFor="kb-search">Search</Label>
        <Input
          id="kb-search" value={search} placeholder="What is the question?"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <Card>
        {articles.isPending && <Skeleton className="m-4 h-24" />}
        {articles.isError && (
          <ErrorState error={articles.error} onRetry={() => void articles.refetch()} />
        )}
        {articles.isSuccess && articles.data.content.length === 0 && (
          <EmptyState
            title="Nothing is written down yet"
            description="An article is an answer given once. Nothing ships: the questions people ask here are yours."
          />
        )}
        {articles.isSuccess && articles.data.content.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {articles.data.content.map((article) => (
              <li key={article.id} className="space-y-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 font-medium text-[var(--color-heading)]">
                    <BookOpen className="size-4 text-slate-500" aria-hidden="true" />
                    {article.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      status={article.status}
                      tone={article.status === 'PUBLISHED' ? 'success' : 'neutral'}
                      label={KB_STATUS_LABELS[article.status as KbStatus] ?? article.status}
                    />
                    {article.status !== 'PUBLISHED' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          void (async () => {
                            setError(null)
                            try {
                              await publishArticle.mutateAsync(article.id)
                            } catch (caught) {
                              setError(toApiProblem(caught)?.detail
                                ?? 'That article could not be published')
                            }
                          })()
                        }}
                      >
                        Publish
                      </Button>
                    )}
                    <Button
                      type="button" variant="ghost"
                      aria-label={`Edit ${article.title}`}
                      onClick={() => {
                        setError(null)
                        setDraft({
                          id: article.id,
                          slug: article.slug,
                          title: article.title,
                          summary: article.summary ?? '',
                          body: article.body,
                          categoryId: article.categoryId ?? '',
                        })
                      }}
                    >
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit
                    </Button>
                    {article.status !== 'ARCHIVED' && (
                      <Button
                        type="button" variant="outline"
                        onClick={() => {
                          setError(null)
                          setArchiving({ id: article.id, title: article.title })
                        }}
                      >
                        Archive
                      </Button>
                    )}
                  </div>
                </div>
                {article.summary && <p className="text-sm text-slate-700">{article.summary}</p>}
                <p className="whitespace-pre-wrap text-sm text-slate-600">{article.body}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                  <span>{article.feedbackNote}</span>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void rateArticle.mutateAsync({ id: article.id, helpful: true })}
                  >
                    <ThumbsUp className="size-3.5" aria-hidden="true" />
                    This helped
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void rateArticle.mutateAsync({ id: article.id, helpful: false })}
                  >
                    <ThumbsDown className="size-3.5" aria-hidden="true" />
                    It did not
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => { if (!open) setArchiving(null) }}
        title={`Archive ${archiving?.title ?? 'this article'}?`}
        description="It stops being searchable and keeps its history — a ticket resolved by pointing at it still refers to it."
        confirmLabel="Archive it"
        loading={archiveArticle.isPending}
        error={error}
        onConfirm={() => {
          if (!archiving) return
          setError(null)
          archiveArticle.mutate(archiving.id, {
            onSuccess: () => setArchiving(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That article could not be archived.'),
          })
        }}
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        size="lg"
        title={draft?.id ? 'Edit article' : 'New article'}
        submitLabel="Save article"
        loading={saveArticle.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveArticle.mutateAsync({
                id: draft.id,
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  categoryId: draft.categoryId || null,
                  slug: draft.slug,
                  title: draft.title,
                  summary: draft.summary || null,
                  body: draft.body,
                  tags: [],
                },
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That article could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="kb-slug">Short name</Label>
              <Input
                id="kb-slug" value={draft.slug}
                onChange={(event) => setDraft({ ...draft, slug: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">Used in the address. Unique.</p>
            </div>
            <div>
              <Label htmlFor="kb-category">Category</Label>
              <Select
                id="kb-category"
                className="w-full"
                value={draft.categoryId}
                onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
              >
                <option value="">No category</option>
                {categories.data?.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="kb-title">Title</Label>
              <Input
                id="kb-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="kb-summary">One line</Label>
              <Input
                id="kb-summary" value={draft.summary}
                onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="kb-body">The answer</Label>
              <Input
                id="kb-body" value={draft.body}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
